//LAZY import {fabric} from 'fabric';
import * as log from 'loglevel';

import swapColorFilter from './swap-color-filter';
import lumaColorFilter from './luma-color-filter';

/**
 * Customer-facing shading-style presets for luma mode. The options editor stores only the chosen
 * preset name (params.colorize.preset); the renderer resolves it to the underlying ColorizeLuma
 * filter values here. `balanced` is the default for a missing or unknown preset.
 * Keep in sync with the dropdown options in OverlayLayerForm.LUMA_PRESETS.
 */
export const LUMA_PRESETS = {
    balanced: { strength: 1.0, shadows: 0.08, highlights: 1.0, highlightMix: 0.2, highlightStart: 0.72 },
    bright:   { strength: 1.0, shadows: 0.1,  highlights: 1.1, highlightMix: 0.35, highlightStart: 0.6 },
    deep:     { strength: 1.1, shadows: 0.02, highlights: 0.95, highlightMix: 0.1, highlightStart: 0.8 },
};

export const DEFAULT_LUMA_PRESET = 'balanced';

class ColorizeUtil {

    static getKey(color, colorize){
        return JSON.stringify({
            color: color,
            colorize: colorize || { mode: 'swap' }
        });
    }

    static colorize(object, color, layer, params){

        if(object.type !== 'image'){
            ColorizeUtil.colorizeFabricObject(object, color);
            return Promise.resolve(object);
        }

        let colorize = params.colorize || { mode: 'swap' };

        if(colorize.mode === 'mask'){
            return ColorizeUtil.colorizeImageMask(object, color, layer, params);
        }

        if(colorize.mode === 'luma'){
            ColorizeUtil.colorizeImageLuma(object, color, params);
            return Promise.resolve(object);
        }

        ColorizeUtil.colorizeImageSwap(object, color);
        return Promise.resolve(object);

    }

    static colorizeFabricObject(object, color){

        // SVG group
        if(object.type === 'group'){
            let objects = object.getObjects();

            for (let i = 0; i < objects.length; i++) {
                const path = objects[i];
                path.set('fill', color);
            }
        }
        // SVG single path
        else if(object.type === 'path' || object.type === 'circle'){
            object.set('fill', color);
        }

    }

    static colorizeImageSwap(object, color){

        // img object
        if(object.type === 'image' && object.width && object.height){

            let filter;

            // only init once
            if(object.filters && object.filters.length){
                ColorizeUtil.removeColorizeFilters(object, 'SwapColor');
                filter = object.filters.find(f => f.type === 'SwapColor');
            } else {
                object.filters = [];
            }

            if(!filter){
                // filter not yet loaded
                if(typeof fabric.Image.filters.SwapColor === 'undefined'){
                    // call the setup function
                    // INFO: because we lazy load fabric - we wrap the filter in a setup function
                    swapColorFilter(fabric);
                }

                filter = new fabric.Image.filters.SwapColor();
                filter.colorSource = '#fff'; // always replace white
                // add to fabric filters
                object.filters.push(filter);
            }

            // update new color
            filter.colorDestination = color;

            // apply color swap
            object.applyFilters();
        }

    }

    static colorizeImageLuma(object, color, params){

        // img object
        if(object.type === 'image' && object.width && object.height){

            let colorize = params.colorize || {};
            let filter;

            // only init once
            if(object.filters && object.filters.length){
                ColorizeUtil.removeColorizeFilters(object, 'ColorizeLuma');
                filter = object.filters.find(f => f.type === 'ColorizeLuma');
            } else {
                object.filters = [];
            }

            if(!filter){
                // filter not yet loaded
                if(typeof fabric.Image.filters.ColorizeLuma === 'undefined'){
                    // call the setup function
                    // INFO: because we lazy load fabric - we wrap the filter in a setup function
                    lumaColorFilter(fabric);
                }

                filter = new fabric.Image.filters.ColorizeLuma();
                object.filters.push(filter);
            }

            // resolve the named shading-style preset to the underlying luma controls
            let preset = LUMA_PRESETS[colorize.preset] || LUMA_PRESETS[DEFAULT_LUMA_PRESET];

            // update new color and resolved luma controls
            filter.color = color;
            filter.strength = preset.strength;
            filter.shadows = preset.shadows;
            filter.highlights = preset.highlights;
            filter.highlightMix = preset.highlightMix;
            filter.highlightStart = preset.highlightStart;

            // apply colorized luma
            object.applyFilters();
        }

    }

    static removeColorizeFilters(object, keepType){
        object.filters = object.filters.filter(filter => {
            return filter.type === keepType || (filter.type !== 'SwapColor' && filter.type !== 'ColorizeLuma');
        });
    }

    static colorizeImageMask(object, color, layer, params){
        let colorize = params.colorize || {};

        if(!colorize.maskUrl){
            log.warn('missing colorize.maskUrl for layer: ', layer.name);
            return Promise.resolve(object);
        }

        return ColorizeUtil.loadImage(colorize.maskUrl, layer, '_colorizeMaskImage')
            .then((maskImage) => {
                if(!maskImage){
                    return object;
                }

                let shadePromise = colorize.shadeUrl
                    ? ColorizeUtil.loadImage(colorize.shadeUrl, layer, '_colorizeShadeImage')
                    : Promise.resolve(null);

                return shadePromise.then((shadeImage) => {
                    let width = maskImage.naturalWidth || maskImage.width || object.width;
                    let height = maskImage.naturalHeight || maskImage.height || object.height;
                    let canvas = layer._colorizeCanvas;

                    if(!canvas){
                        canvas = document.createElement('canvas');
                        layer._colorizeCanvas = canvas;
                    }

                    if(canvas.width !== width || canvas.height !== height){
                        canvas.width = width;
                        canvas.height = height;
                    }

                    let ctx = canvas.getContext('2d');
                    ctx.clearRect(0, 0, width, height);
                    ctx.globalCompositeOperation = 'source-over';
                    ctx.fillStyle = color;
                    ctx.fillRect(0, 0, width, height);

                    let maskCanvas = ColorizeUtil.getAlphaMaskCanvas(maskImage, layer, colorize);

                    ctx.globalCompositeOperation = 'destination-in';
                    ctx.drawImage(maskCanvas, 0, 0, width, height);

                    if(shadeImage){
                        ctx.globalCompositeOperation = colorize.blendMode || 'multiply';
                        ctx.globalAlpha = colorize.shadeAlpha === undefined ? 1 : colorize.shadeAlpha;
                        ctx.drawImage(shadeImage, 0, 0, width, height);
                        ctx.globalAlpha = 1;

                        ctx.globalCompositeOperation = 'destination-in';
                        ctx.drawImage(maskCanvas, 0, 0, width, height);
                    }

                    ctx.globalCompositeOperation = 'source-over';

                    object.filters = [];
                    object.setElement(canvas);
                    object.set({
                        width: width,
                        height: height,
                        dirty: true
                    });

                    return object;
                });
            });
    }

    static loadImage(url, layer, cacheKey){
        let loaded = layer[cacheKey];

        if(loaded && loaded._url === url){
            return Promise.resolve(loaded);
        }

        return new Promise((resolve) => {
            let image = new Image();

            image.crossOrigin = 'anonymous';
            image.onload = () => {
                image._url = url;
                layer[cacheKey] = image;

                if(cacheKey === '_colorizeMaskImage'){
                    delete layer._colorizeAlphaMaskCanvas;
                    delete layer._colorizeAlphaMaskUrl;
                }

                resolve(image);
            };
            image.onerror = () => {
                log.warn('colorize image url not found: ', url);
                resolve(null);
            };
            image.src = url;
        });
    }

    static getAlphaMaskCanvas(maskImage, layer, colorize){
        let url = maskImage._url || '';
        let mode = colorize.maskChannel || 'alpha'; // backend defaults to alpha

        if(layer._colorizeAlphaMaskCanvas && layer._colorizeAlphaMaskUrl === url && layer._colorizeAlphaMaskMode === mode){
            return layer._colorizeAlphaMaskCanvas;
        }

        let width = maskImage.naturalWidth || maskImage.width;
        let height = maskImage.naturalHeight || maskImage.height;
        let canvas = document.createElement('canvas');

        canvas.width = width;
        canvas.height = height;

        let ctx = canvas.getContext('2d');
        ctx.drawImage(maskImage, 0, 0, width, height);

        if(mode !== 'alpha'){
            let imageData = ctx.getImageData(0, 0, width, height);
            let data = imageData.data;

            for (let i = 0; i < data.length; i += 4) {
                let luma = Math.round((data[i] * 0.299) + (data[i + 1] * 0.587) + (data[i + 2] * 0.114));
                data[i + 3] = Math.round(data[i + 3] * (luma / 255));
                data[i] = 255;
                data[i + 1] = 255;
                data[i + 2] = 255;
            }

            ctx.putImageData(imageData, 0, 0);
        }

        layer._colorizeAlphaMaskCanvas = canvas;
        layer._colorizeAlphaMaskUrl = url;
        layer._colorizeAlphaMaskMode = mode;

        return canvas;
    }

}

export default ColorizeUtil;
